import React, { ReactNode } from "react";
import { alpha, IconButton, styled, useScrollTrigger } from "@mui/material";
import BurgerIcon from "@icons/myicons/sidebar/burger.svg?react";
import CloseIcon from "@icons/myicons/sidebar/close.svg?react";
import { MandatorConditional } from "@helpers";
import { Footer } from "../Footer";
import { Profile } from "../Profile";
import { UserIdField } from "../UserIdField";

const BAR_HEIGHT = 54;

const Container = styled("div", {
  shouldForwardProp: (prop) => prop !== "collapsed" && prop !== "isFloating",
})<{ collapsed?: boolean; isFloating?: boolean }>(
  ({ theme, collapsed, isFloating }) => ({
    position: "sticky",
    top: 0,
    zIndex: theme.zIndex.drawer + 1,
    display: "flex",
    flexDirection: "column",
    height: collapsed ? BAR_HEIGHT : "100dvh",
    backgroundColor: theme.palette.background.sidebar,
    overflow: "hidden",
    padding: collapsed ? theme.spacing(0, 4) : theme.spacing(0, 4, 6, 4),
    transition: theme.transitions.create(
      ["height", "top", "margin-left", "margin-right", "border-radius"],
      {
        duration: theme.transitions.duration.shorter,
        easing: theme.transitions.easing.easeInOut,
      }
    ),
    ...(isFloating && {
      top: 10,
      marginLeft: 12,
      marginRight: 12,
      borderRadius: 6,
    }),
  })
);

const HeaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  height: BAR_HEIGHT,
  alignItems: "center",
  padding: theme.spacing(2, 0),
}));

const Scroll = styled("div", {
  shouldForwardProp: (prop) => prop !== "collapsed",
})<{ collapsed?: boolean }>(({ theme, collapsed }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  gap: theme.spacing(6),
  overflowY: collapsed ? "hidden" : "auto",
  msOverflowStyle: "none",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

const Body = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  gap: theme.spacing(2),
}));

const MenuButton = styled(IconButton)(({ theme }) => ({
  color: theme.appSidebar?.openButton?.color,
  "&:hover": {
    backgroundColor:
      theme.appSidebar?.openButton?.hoverBackground ??
      alpha(theme.palette.button.textButton.hover, 0.1),
  },
  "&:active": {
    backgroundColor:
      theme.appSidebar?.openButton?.activeBackground ??
      alpha(theme.palette.button.textButton.hover, 0.2),
  },
}));

export interface SidebarResponsiveProps {
  header: ReactNode;
  children: ReactNode;
  collapsed: boolean;
  onMenuOpen: () => void;
}

export const SidebarResponsive = ({
  header,
  children,
  collapsed,
  onMenuOpen,
}: SidebarResponsiveProps) => {
  const isScrolled = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return (
    <Container collapsed={collapsed} isFloating={collapsed && isScrolled}>
      <HeaderContainer>
        {header}
        <MenuButton onClick={onMenuOpen}>
          {collapsed ? <BurgerIcon /> : <CloseIcon />}
        </MenuButton>
      </HeaderContainer>
      <Scroll collapsed={collapsed}>
        <Body>{children}</Body>
        <MandatorConditional showFor={["kpmg-germany"]}>
          <UserIdField />
        </MandatorConditional>
        <Profile />
      </Scroll>
      <Footer />
    </Container>
  );
};
